import React, { useState } from 'react';
import { PageHeader } from 'components';
import { useTranslation } from 'react-i18next';
import MySettingsIcon from 'icons/img/MySettingsIcon.svg';
import { BasicInformation } from './BasicInformation';
import { Notifications } from './Notifications';

// Constants

import styled from 'styled-components';
import { JBTRoutes } from 'constants/routes';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import BellIcon from 'icons/indexed-icons/Bell';
import UserIcon from 'icons/indexed-icons/User';
import theme, { themeColors } from 'themes';
import { IconExpand } from 'icons/indexed-icons/IconExpand';
import { IconCollapse } from 'icons/indexed-icons/IconCollapse';
import breakpoints from 'constants/breakpoints';
export const Container = styled.main`
  position: relative;
  width: 100%;
  padding: 0 2rem;
  display: flex;

  @media (max-width:${breakpoints.size.md}px) {
    padding: 0px 1rem;
    top: 0;
  }
`;
const SideBarMenu = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  width: 20%;
  border-right: 1px solid ${theme.colors.lightGrey3};
  min-width: 14rem;
  max-width: 25%;
  @media (max-width: ${breakpoints.size.md}px) {
    display: none;
  }
`;
const SideBarHeader = styled.div`
  color: ${theme.colors.text.lightBlack};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 1rem;
`;
const SettingsBoard = styled.div`
  padding-top: 12px;
  width: 100%;
  height: 100%;
  margin-left: 2%;
  @media (max-width: ${breakpoints.size.md}px) {
    margin-left: 0;
  }
`;
const MenuItemWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
  cursor: pointer;
`;

const MenuItem = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? theme.colors.primaryBlue5 : theme.colors.text.lightBlack)};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: ${(props) => (props.isActive ? '700' : '400')};
  line-height: 16px;
`;
const MenuIcon = styled.div`
  position: relative;
  top: -3px;
  margin-right: 1rem;
`;

const SettingsWrapper = styled.div`
  width: 100%;
  display: flex;
  height: 100%;
  flex-direction: column;
`;

const PopupMain = styled.div`
  position: relative;
`;

const DocsNavMobile = styled.div`
  margin: 1rem 1rem 0 1rem;
  @media (min-width: ${breakpoints.size.md}px) {
    display: none;
  }
`;

const IconMobile = styled.div<{ isSideNavOpen: boolean }>`
  cursor: pointer;
`;

const PopupContainer = styled.div`
  position: absolute;
  min-width: 16rem;
  padding: 1rem 1rem 3rem;
  border-radius: 0px 0px 8px 8px;
  box-shadow: 0px 4px 14px 0px rgba(0, 0, 0, 0.15);
  top: 25px;
  left: 0;
  z-index: 10;
  background-color: ${themeColors.white};
`;

const UserSettings = (): JSX.Element => {
  const { t } = useTranslation(['common']);
  const history = useHistory();
  const [showBasicInformation, setShowBasicInformation] = useState<boolean>(false);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const match = useRouteMatch();
  const urlParam: { userId?: string } = useParams();
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  React.useEffect(() => {
    if (match.path.includes('settings/basic-information')) {
      setShowBasicInformation(true);
      setShowNotifications(false)
    } else if (match.path.includes('settings/notifications')) {
      setShowNotifications(true);
    }
  }, [match.path]);
  const handleSettingMenuItemClick = (menuItem: string): void => {
    switch (menuItem) {
      case 'basic':
        if (!showBasicInformation) {
          setShowBasicInformation(true);
          setShowNotifications(false);
          history.push(JBTRoutes.userSettingsBasic.replace(':userId', urlParam.userId as string));
        }
        if (mobileNavOpen) {
          setMobileNavOpen(false);
        }
        break;
      case 'notify':
        if (!showNotifications) {
          setShowNotifications(true);
          setShowBasicInformation(false);
          history.push(
            JBTRoutes.userSettingsNotification.replace(':userId', urlParam.userId as string)
          );
        }
        if (mobileNavOpen) {
          setMobileNavOpen(false);
        }
        break;
      default:
        break;
    }
  };

  const handleToggle = () => {
    setMobileNavOpen(!mobileNavOpen);
  };

  const NavItems = (
    <>
      <MenuItemWrapper
        onClick={() => {
          handleSettingMenuItemClick('basic');
        }}
      >
        <MenuIcon>
          <UserIcon />
        </MenuIcon>
        <MenuItem isActive={showBasicInformation}>{t('basic_information')}</MenuItem>
      </MenuItemWrapper>
      <MenuItemWrapper
        onClick={() => {
          handleSettingMenuItemClick('notify');
        }}
      >
        <MenuIcon>
          <BellIcon />
        </MenuIcon>
        <MenuItem isActive={showNotifications}>{t('notifications')}</MenuItem>
      </MenuItemWrapper>
    </>
  );

  const mobileSideNav = (
    <>
      <SideBarHeader>{t('getting_started')}</SideBarHeader>
      {NavItems}
    </>
  );

  const renderMobileView = () => (
    <PopupMain>
      <IconMobile onClick={handleToggle} isSideNavOpen={mobileNavOpen}>
        {mobileNavOpen ? (
          <IconExpand strokeColor={themeColors.primaryBlue5} />
        ) : (
          <IconCollapse strokeColor={themeColors.primaryBlue5} />
        )}
      </IconMobile>
      {mobileNavOpen && <PopupContainer>{mobileSideNav}</PopupContainer>}
    </PopupMain>
  );

  return (
    <>
      <PageHeader heading={t('settings')} icon={MySettingsIcon} />
      <DocsNavMobile className="setting-icon-box">{renderMobileView()}</DocsNavMobile>
      <Container>
        <SideBarMenu>
          <SideBarHeader>{t('getting_started')}</SideBarHeader>
          {NavItems}
        </SideBarMenu>
        <SettingsWrapper>
          <SettingsBoard>
            <BasicInformation isActive={showBasicInformation} />
            <Notifications isActive={showNotifications} />
          </SettingsBoard>
        </SettingsWrapper>
      </Container>
    </>
  );
};

export default UserSettings;
